import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import Table from "../../../core/common/dataTable/index";
import {
  clearError,
  deleteNOCPassportRequest,
  getAllNocPassport,
  INocPassport,
  setCurrentRequest,
} from "../../../core/data/redux/nocPassportSlice";
import type { AppDispatch, RootState } from "../../../core/data/redux/store";
import DeleteModal from "../../../core/modals/deleteModal";
import { formatEmpIdForDisplay } from "../../../utils/empIdUtils";
import { all_routes } from "../../router/all_routes";
import SyncNocRequest from "./sync-noc-request";
import DateRangeFilter from "../../../core/common/DateRangeFilter";
import RequestStatusBadge from "./RequestStatusBadge";
import RequestStatusFilter from "./RequestStatusFilter";
import { isAdminOrSubAdmin, isSupportViewer } from "../../../utils/permissionUtils";
import Api from "../../../core/data/redux/utils/apiCall";
import { API_URLS } from "../../../core/data/redux/apis/urls";


interface NOCPassportProps { }

const user: any = localStorage.getItem("user");
const userJson = user && user !== "undefined" ? JSON.parse(user) : {};
let userType: any = userJson?.user_type || userJson?.role;
const isManagement = isAdminOrSubAdmin(userType) || isSupportViewer(userType);

// --- Utility Functions (Standardized) ---

// ⚡ Consistent Date Format: DD-MM-YYYY
const formatDate = (dateString: string | null) => {
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "-";
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch (e) {
    return "-";
  }
};

// Helper function to format D&AR status
const formatPassportDnarStatus = (val: string | undefined | null) => {
  if (!val) return "-";
  const s = String(val).trim();
  if (s === "C" || s === "D&AR comp..." || s.toLowerCase().includes("comp")) return "D&AR completed";
  if (s === "P" || s.toLowerCase().includes("pend")) return "D&AR pending";
  if (s === "N" || s === "No" || s.toLowerCase().includes("no d&ar")) return "No D&AR Case";
  if (s === "M") return "Major Penalty";
  if (s === "J") return "Minor Penalty";
  if (s === "Y") return "Yes";
  return s;
};

// Helper function to get NOC Applied For text
const getNOCAppliedForText = (value: string | undefined | null, record?: any) => {
  const val = value || record?.nOCAppliedFor || record?.nocAppliedFor || record?.appliedFor || record?.typeOfNoc || record?.natureDetails?.natureTitle;
  if (!val) return "-";
  const s = String(val).trim();
  switch (s) {
    case "0":
    case "01":
    case "FP":
      return "Fresh Passport";
    case "1":
    case "02":
    case "RP":
      return "Renewal of Passport";
    case "2":
    case "03":
    case "VS":
    case "VISA":
    case "visa":
      return "VISA";
    default:
      return s;
  }
};

// --- Component ---

const NOCPassport = (props: NOCPassportProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const routes = all_routes;

  // Redux state
  const {
    data: requests,
    loading,
    error,
    total,
    deleteLoading,
  } = useSelector((state: RootState) => state.nocPassportState);

  // Local state
  const [deleteModalId, setDeleteModalId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
  });

  // Update total count from Redux state
  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      total: total || 0,
    }));
  }, [total]);

  // Data fetching logic
  const fetchData = (
    page = pagination.page,
    limit = pagination.limit,
    query = searchQuery,
    requestStatus = selectedStatus,
    dateStart = startDate,
    dateEnd = endDate
  ) => {
    const params: any = {
      page,
      limit,
      query,
      orderColumn: "created_at",
      orderBy: "DESC",
    };
    if (dateStart) params.startDate = dateStart;
    if (dateEnd) params.endDate = dateEnd;
    if (requestStatus) params.requestStatus = requestStatus;
    dispatch(getAllNocPassport(params));
  };

  // Fetch data on component mount/pagination change
  useEffect(() => {
    fetchData();
  }, [pagination.page, pagination.limit]);

  // ⚡ New: Toast for Redux errors
  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Handle pagination change
  const handlePagination = (newPagination: any, filters: any, sorter: any) => {
    const page = newPagination.current || 1;
    const pageSize = newPagination.pageSize || pagination.limit;
    setPagination((prev) => ({ ...prev, page, limit: pageSize }));
    fetchData(page, pageSize, searchQuery, selectedStatus, startDate, endDate);
  };



  // Handle search action
  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setPagination((prev) => ({ ...prev, page: 1 }));
    fetchData(1, pagination.limit, query, selectedStatus, startDate, endDate);
  };

  // Handle filter apply
  const handleApplyFilter = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    fetchData(1, pagination.limit, searchQuery, selectedStatus, startDate, endDate);
  };

  // Handle clear filter
  const handleClearFilter = () => {
    setStartDate("");
    setEndDate("");
    setSelectedStatus("");
    setPagination((prev) => ({ ...prev, page: 1 }));
    fetchData(1, pagination.limit, searchQuery, "", "", "");
  };

  // Handle delete confirmation
  const handleDeleteClick = (id: number) => {
    setDeleteModalId(id);
  };

  // Handle delete confirmation response
  const handleDeleteConfirm = async (response: string) => {
    if (response === "YES" && deleteModalId) {
      try {
        await dispatch(
          deleteNOCPassportRequest({ id: deleteModalId })
        ).unwrap();
        toast.success("NOC request deleted successfully!");
        fetchData(pagination.page, pagination.limit, searchQuery, selectedStatus, startDate, endDate);
      } catch (error) {
        console.error("Failed to delete request:", error);
        toast.error("Failed to delete NOC request. Please try again.");
      }
    }
    setDeleteModalId(null);
  };

  // Handle view details click
  const handleViewDetailsClick = (record: INocPassport) => {
    dispatch(setCurrentRequest(record.id));
  };

  // Handle edit button click
  const handleEditClick = (record: INocPassport) => {
    dispatch(setCurrentRequest(record.id));
  };

  // Columns definition matching SAP Passport Previous Requests Overview
  const columns = [
    {
      title: "Sr. No.",
      dataIndex: "id",
      width: 70,
      render: (_: any, __: any, index: number) => (
        <div>
          {pagination.page
            ? index + pagination.page * pagination.limit - pagination.limit + 1
            : index + 1}
        </div>
      ),
    },
    {
      title: "Reference Number",
      dataIndex: "referenceNumber",
      sorter: (a: INocPassport, b: INocPassport) =>
        (a.referenceNumber || "").localeCompare(b.referenceNumber || ""),
      render: (text: string) => (
        <span className="fw-medium font-monospace">{text || "-"}</span>
      ),
    },
    {
      title: "place of posting",
      dataIndex: "placeOfPosting",
      sorter: (a: any, b: any) =>
        (a.placeOfPosting || a.placeofPosting || "").localeCompare(
          b.placeOfPosting || b.placeofPosting || ""
        ),
      render: (text: string, record: any) => text || record.placeofPosting || "-",
    },
    {
      title: "NOC applied for",
      dataIndex: "nOCAppliedFor",
      sorter: (a: any, b: any) => {
        const textA = getNOCAppliedForText(a.nOCAppliedFor, a);
        const textB = getNOCAppliedForText(b.nOCAppliedFor, b);
        return textA.localeCompare(textB);
      },
      render: (value: string, record: any) => (
        <span className="fw-medium">{getNOCAppliedForText(value, record)}</span>
      ),
    },
    {
      title: "Request Status",
      dataIndex: "requestStatus",
      render: (status: string, record: any) => (
        <RequestStatusBadge status={status || record.status} />
      ),
    },
    {
      title: "Request Creation Date",
      dataIndex: "requestCreationDate",
      sorter: (a: any, b: any) =>
        new Date(a.requestCreationDate || a.created_at || a.requestCreationDat || 0).getTime() -
        new Date(b.requestCreationDate || b.created_at || b.requestCreationDat || 0).getTime(),
      render: (date: string | null, record: any) =>
        formatDate(date || record.created_at || record.requestCreationDat || record.sap_created_at),
    },
    {
      title: "Remarks by Controlling",
      dataIndex: "remarks",
      render: (text: string, record: any) =>
        text || record.controllingRemarks || record.Remarks || "-",
    },
    {
      title: "D&AR penalty effecti",
      dataIndex: "dnARPenaltyEffecti",
      render: (text: string, record: any) => {
        const val = text || record.dnARPenaltyEffective || record.DnARPenaltyEffecti || record.dandARPenaltyEffective;
        return formatDate(val);
      },
    },
    {
      title: "D&AR Status",
      dataIndex: "dnARStatus",
      render: (text: string, record: any) => {
        const val = text || record.DnARStatus || record.dandARStatus || record.DandARStatus;
        return formatPassportDnarStatus(val);
      },
    },
    {
      title: "Remarks (if rejected)",
      dataIndex: "rejectionRemarks",
      render: (text: string, record: any) => {
        const val = text || record.hRemarks || record.HRemarks || record.remarks1 || record.Remarks1;
        return val || "-";
      },
    },
    {
      title: "Last/Latest Approver",
      dataIndex: "approverName",
      sorter: (a: any, b: any) =>
        (a.approverName || a.lastApprover || "").localeCompare(b.approverName || b.lastApprover || ""),
      render: (text: string, record: any) => {
        const name = text || record.lastApprover || record.approver?.name || record.approverName;
        const id = record.approverUserID || record.approverId || record.approver?.empId;
        if (name && id) return `${name} (${formatEmpIdForDisplay(id)})`;
        if (name) return name;
        if (id) return formatEmpIdForDisplay(id);
        return "-";
      },
    },
  ];

  // Always include Actions column so users can view/edit draft requests
  columns.push({
    title: "Actions",
    dataIndex: "actions",
    render: (text: string, record: INocPassport) => {
      const isDraft = Boolean(
        record.is_draft ||
        (record as any).isDraft ||
        record.requestStatus === "DRAFT" ||
        record.requestStatus === "draft" ||
        record.requestStatus === "-" ||
        !record.requestStatus
      );

      return (
        <div className="action-icon d-inline-flex align-items-center">
          <Link
            to={`${routes.noc.passport}/details/${record.id}`}
            className="text-primary me-2"
            title="View Details"
            onClick={() => handleViewDetailsClick(record)}
          >
            <i className="ti ti-eye fs-16"></i>
          </Link>
          {isDraft && (
            <>
              <Link
                to={`${routes.noc.passport}/edit/${record.id}`}
                className="text-success me-2"
                title="Edit Request"
                onClick={() => handleEditClick(record)}
              >
                <i className="ti ti-edit fs-16"></i>
              </Link>
              <button
                type="button"
                className="btn btn-link text-danger p-0"
                title="Delete Request"
                onClick={() => handleDeleteClick(record.id)}
                disabled={deleteLoading}
                data-bs-toggle="modal"
                data-bs-target="#delete_modal"
              >
                <i className="ti ti-trash fs-16"></i>
              </button>
            </>
          )}
        </div>
      );
    },
  } as any);

  // Clear error on component unmount
  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">NOC for Passport / Visa</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.employeeDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.noc.main}>NOC Services</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Passport / Visa
                </li>
              </ol>
            </nav>
          </div>
        </div>

        <div className="card">
          {/* Card Header: Title on Left, Action Buttons on Right */}
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-3 py-3">
            <h4 className="mb-0 fw-bold">NOC Applications</h4>
            <div className="d-flex align-items-center gap-2">
              {!isManagement && (
                <Link
                  to={`${routes.noc.passport}/create`}
                  className="btn btn-primary text-nowrap"
                >
                  <i className="ti ti-plus me-1"></i>
                  Create New Request
                </Link>
              )}
              <SyncNocRequest nocType={'VISA-PASSPORT'} onSyncSuccess={() => fetchData()} />
            </div>
          </div>

          <div className="card-body">
            {/* Dedicated Modern Filter Toolbar */}
            <div className="p-3 bg-light rounded-3 border mb-3">
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <RequestStatusFilter
                    value={selectedStatus}
                    onChange={setSelectedStatus}
                  />
                  <DateRangeFilter
                    startDate={startDate}
                    endDate={endDate}
                    onChangeStart={setStartDate}
                    onChangeEnd={setEndDate}
                    onApply={handleApplyFilter}
                    onClear={handleClearFilter}
                    variant="inline"
                  />
                </div>
              </div>
            </div>
            <Table
              columns={columns}
              dataSource={requests}
              Selection={false}
              paginationConfig={{
                current: pagination.page,
                pageSize: pagination.limit,
                total: pagination.total || 0,
                showSizeChanger: true,
                pageSizeOptions: ["10", "25", "50", "100"],
                showTotal: (total: number, range: [number, number]) =>
                  `${range[0]}-${range[1]} of ${total} items`,
              }}
              handlePagination={handlePagination}
              loading={loading}
              handleSearch={handleSearch}
            />
          </div>
        </div>

        {/* Delete Modal */}
        <DeleteModal handleDelete={handleDeleteConfirm} />
      </div>
    </div>
  );
};

export default NOCPassport;
